import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { throttle } from 'lodash';
import { useSelector } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import {
  VerifiedUser,
  Send,
  Warning,
  Close
} from '@mui/icons-material';
import MiniDrawer from '../../components/Sidebar/TemporaryDrawer';
import ResponsiveAppBar from '../../components/AppBar/AppBar';
import userVerification from '../../utils/userVerification';
import useAuth from '../../hooks/useAuth';
import useLogout from '../../hooks/logout';
import { useMediaQuery, useTheme } from '@mui/system';

const DashboardLayout = () => {
  const { tokenCookies } = useAuth();
  const handleLogout = useLogout();
  const { user, token } = useSelector((state) => state.authen);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));
  const [open, setOpen] = useState(!isMobile);
  const { firstCall } = useSelector((state) => state.authen);
  const sidebarRef = useRef(null);
  const [collapse, setCollapse] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(isMobile ? 0 : 240);
  const [showBanner, setShowBanner] = useState(true);
  const [isVerifying, setIsVerifying] = useState(false);
  const location = useLocation();

  const isVerified = user?.isVerified;
  const bannerHeight = useMemo(() => (isVerified || !showBanner ? 0 : 70), [isVerified, showBanner]);
  const mainContentMargin = useMemo(() => `calc(${bannerHeight}px + 64px)`, [bannerHeight]);

  const handleLogoutClick = useCallback(() => handleLogout(), [handleLogout]);

  const handleVerify = useCallback(
    throttle(async () => {
      if (user?.email && !isVerifying) {
        setIsVerifying(true);
        try {
          await userVerification.verify(user.email);
          // You might want to show a success message here
        } catch (error) {
          console.error('Verification failed:', error);
        } finally {
          setTimeout(() => setIsVerifying(false), 2000);
        }
      }
    }, 5000, { leading: true, trailing: false }),
    [user?.email, isVerifying]
  );

  const handleDrawerToggle = useCallback(() => {
    setOpen(prev => !prev);
  }, []);

  const handleCloseBanner = useCallback(() => {
    setShowBanner(false);
  }, []);

  // Auto-hide banner on mobile after some time
  useEffect(() => {
    if (isMobile && !isVerified) {
      const timer = setTimeout(() => {
        setShowBanner(false);
      }, 10000); // Hide after 10 seconds on mobile
      return () => clearTimeout(timer);
    }
  }, [isMobile, isVerified]);

  // Responsive sidebar width
  useEffect(() => {
    if (isMobile) {
      setSidebarWidth(0);
    } else if (isTablet) {
      setSidebarWidth(collapse ? 64 : 200);
    } else {
      setSidebarWidth(collapse ? 64 : 240);
    }
  }, [isMobile, isTablet, collapse]);

  useEffect(() => {
    if (!token || user?.status === 0) {
      handleLogout();
    }
  }, [token, user?.status, handleLogout]);

  // Enhanced verification banner with modern design
  const renderVerificationBanner = () => (
    <AnimatePresence>
      {!isVerified && !firstCall && showBanner && (
        <motion.div
          initial={{ opacity: 0, y: -70, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -70, scale: 0.95 }}
          transition={{
            type: "spring",
            stiffness: 400,
            damping: 25,
            duration: 0.4
          }}
          style={{
            position: 'fixed',
            top: '64px',
            left: 0,
            right: 0,
            zIndex: 1200,
          }}
        >
          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(255, 193, 7, 0.1) 0%, rgba(255, 152, 0, 0.15) 100%)',
              backdropFilter: 'blur(12px)',
              borderBottom: '1px solid rgba(255, 193, 7, 0.2)',
              boxShadow: '0 4px 20px rgba(255, 193, 7, 0.1)',
              height: bannerHeight,
              transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: { xs: 2, sm: 3, md: 4 },
                py: 2,
                height: '100%',
                maxWidth: '1200px',
                mx: 'auto',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                >
                  <Box
                    sx={{
                      width: 32,
                      height: 32,
                      borderRadius: '50%',
                      background: 'linear-gradient(45deg, #ff9800, #f57c00)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(255, 152, 0, 0.3)',
                    }}
                  >
                    <Warning sx={{ color: 'white', fontSize: 18 }} />
                  </Box>
                </motion.div>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body1"
                    sx={{
                      fontWeight: 600,
                      color: 'rgba(0, 0, 0, 0.87)',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      lineHeight: 1.4,
                    }}
                  >
                    Email Verification Required
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'rgba(0, 0, 0, 0.7)',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      display: { xs: 'none', sm: 'block' }
                    }}
                  >
                    Please check your inbox and verify your email to access all features
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleVerify}
                  disabled={isVerifying}
                  style={{
                    background: isVerifying
                      ? 'linear-gradient(45deg, #66bb6a, #4caf50)'
                      : 'linear-gradient(45deg, #2196f3, #1976d2)',
                    border: 'none',
                    borderRadius: '12px',
                    color: 'white',
                    padding: '8px 16px',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    cursor: isVerifying ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.3)',
                    transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  {isVerifying ? (
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    >
                      <VerifiedUser sx={{ fontSize: 16 }} />
                    </motion.div>
                  ) : (
                    <Send sx={{ fontSize: 16 }} />
                  )}
                  <span style={{ display: isMobile ? 'none' : 'inline' }}>
                    {isVerifying ? 'Sending...' : 'Resend'}
                  </span>
                </motion.button>

                <IconButton
                  onClick={handleCloseBanner}
                  size="small"
                  sx={{
                    ml: 1,
                    color: 'rgba(0, 0, 0, 0.6)',
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.08)',
                      color: 'rgba(0, 0, 0, 0.87)',
                    },
                    transition: 'all 200ms ease',
                  }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: 'rgba(248, 250, 252, 1)',
        position: 'relative',
      }}
    >
      {/* Enhanced App Bar */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
      >
        <ResponsiveAppBar
          logout={handleLogoutClick}
          tokenCookies={tokenCookies}
          handleDrawerToggle={handleDrawerToggle}
        />
      </motion.div>

      {/* Verification Banner */}
      {renderVerificationBanner()}

      {/* Main Layout */}
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          width: '100vw',
          mt: mainContentMargin,
          transition: 'margin-top 400ms cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
        }}
      >
        {/* Enhanced Sidebar */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          ref={sidebarRef}
          style={{
            flexShrink: 0,
            zIndex: isMobile ? 1300 : 'auto',
          }}
        >
          <MiniDrawer
            isVerified={isVerified}
            open={open}
            setOpen={setOpen}
            sidebarRef={sidebarRef}
            setSidebarWidth={setSidebarWidth}
            collapse={collapse}
            setCollapse={setCollapse}
          />
        </motion.div>

        {/* Main Content Area */}
        <Box
          component="main"
          sx={{
            ml: isMobile ? 0 : `${sidebarWidth}px`,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            transition: 'margin-left 300ms cubic-bezier(0.4, 0, 0.2, 1)',
            position: 'relative',
            minHeight: 0, // Important for flex children
            overflow: 'hidden',
          }}
        >
          {/* Content with smooth transitions */}
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{
              duration: 0.4,
              ease: "easeOut",
              delay: 0.1
            }}
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Outlet />
          </motion.div>
        </Box>
      </Box>

      {/* Floating Action Elements (if needed) */}
      <AnimatePresence>
        {!isVerified && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ delay: 2, duration: 0.5 }}
            style={{
              position: 'fixed',
              bottom: 24,
              right: 24,
              zIndex: 1100,
            }}
          >
            {/* You can add floating notifications or quick actions here */}
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default DashboardLayout;
